import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  TablePagination,
  TableSortLabel,
  TextField,
  Box,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { formatPhoneNumber } from "../../utils/FormatPhone";
import api from "../../api/api";

const getStatusColor = (deletedAt, emailConfirmed) => {
  if (deletedAt) return "bg-red-500";
  return emailConfirmed ? "bg-green-500" : "bg-orange-500";
};

const getCompanyColor = (isCompany) => {
  return isCompany ? "bg-green-500" : "bg-red-500";
};

const formatDate = (date) =>
  format(new Date(date), "dd/MM/yyyy", { locale: ptBR });

const descendingComparator = (a, b, orderBy) => {
  let aValue = a[orderBy];
  let bValue = b[orderBy];

  // Corrigir comparação da cidade
  if (orderBy === "billingAddress") {
    aValue = a.billingAddress?.city?.toLowerCase() || "";
    bValue = b.billingAddress?.city?.toLowerCase() || "";
  }

  if (bValue < aValue) return -1;
  if (bValue > aValue) return 1;
  return 0;
};

const getComparator = (order, orderBy) => {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

const ClientTable = ({ clients, onEdit }) => {
  const [sellers, setSellers] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt");

  // Filtros
  const [filterName, setFilterName] = useState("");
  const [filterEmail, setFilterEmail] = useState("");
  const [filterCity, setFilterCity] = useState("");
  const [filterSeller, setFilterSeller] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  useEffect(() => {
    const fetchSellers = async () => {
      try {
        const sellerIds = [...new Set(clients.map((client) => client.seller))];
        const responses = await Promise.all(
          sellerIds.map((id) =>
            id ? api.get(`sellers/${id}`).then((res) => res.data.seller) : null
          )
        );
        const sellersData = responses.reduce((acc, seller) => {
          if (seller) acc[seller.id] = seller.name;
          return acc;
        }, {});
        setSellers(sellersData);
      } catch (error) {
        console.error("Failed to fetch sellers", error);
      }
    };

    fetchSellers();
  }, [clients]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const filteredClients = useMemo(() => {
    return clients.filter((client) => {
      const sellerName = client.seller ? sellers[client.seller] || "" : "";
      const city = client.billingAddress?.city || "";

      if (filterStatus === "deleted" && !client.deletedAt) return false;
      if (
        filterStatus === "confirmed" &&
        (!client.emailConfirmed || client.deletedAt)
      )
        return false;
      if (
        filterStatus === "unconfirmed" &&
        (client.emailConfirmed || client.deletedAt)
      )
        return false;

      return (
        client.name.toLowerCase().includes(filterName.toLowerCase()) &&
        client.email.toLowerCase().includes(filterEmail.toLowerCase()) &&
        city.toLowerCase().includes(filterCity.toLowerCase()) &&
        sellerName.toLowerCase().includes(filterSeller.toLowerCase())
      );
    });
  }, [
    clients,
    sellers,
    filterName,
    filterEmail,
    filterCity,
    filterSeller,
    filterStatus,
  ]);

  const sortedClients = useMemo(() => {
    return [...filteredClients].sort(getComparator(order, orderBy));
  }, [filteredClients, order, orderBy]);

  const paginatedClients = useMemo(() => {
    return sortedClients.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [sortedClients, page, rowsPerPage]);

  return (
    <div className="flex flex-col">
      <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
        <TextField
          label="Filtrar por Nome"
          variant="outlined"
          value={filterName}
          onChange={(e) => setFilterName(e.target.value)}
          size="small"
        />
        {/* <TextField
          label="Filtrar por Email"
          variant="outlined"
          value={filterEmail}
          onChange={(e) => setFilterEmail(e.target.value)}
          size="small"
        /> */}
        <TextField
          select
          // label="Filtrar por Status"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          SelectProps={{ native: true }}
          size="small"
        >
          <option value="">Todos</option>
          <option value="deleted">Deletado</option>
          <option value="confirmed">Confirmado</option>
          <option value="unconfirmed">Não Confirmado</option>
        </TextField>
        <TextField
          label="Filtrar por Cidade"
          variant="outlined"
          value={filterCity}
          onChange={(e) => setFilterCity(e.target.value)}
          size="small"
        />
        <TextField
          label="Filtrar por Vendedor"
          variant="outlined"
          value={filterSeller}
          onChange={(e) => setFilterSeller(e.target.value)}
          size="small"
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === "name" ? order : false}>
                <TableSortLabel
                  active={orderBy === "name"}
                  direction={orderBy === "name" ? order : "asc"}
                  onClick={() => handleRequestSort("name")}
                >
                  Nome
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="center"
                sortDirection={orderBy === "isCompany" ? order : false}
              >
                <TableSortLabel
                  align="center"
                  active={orderBy === "isCompany"}
                  direction={orderBy === "isCompany" ? order : "asc"}
                  onClick={() => handleRequestSort("isCompany")}
                >
                  Salão
                </TableSortLabel>
              </TableCell>

              <TableCell align="center">Vendedor</TableCell>
              <TableCell align="center">Contato</TableCell>
              {/* <TableCell align="center">Email</TableCell> */}
              <TableCell align="center">Status</TableCell>
              <TableCell
                align="center"
                sortDirection={orderBy === "createdAt" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "createdAt"}
                  direction={orderBy === "createdAt" ? order : "asc"}
                  onClick={() => handleRequestSort("createdAt")}
                >
                  Data de Cadastro
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="center"
                sortDirection={orderBy === "billingAddress" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "billingAddress"}
                  direction={orderBy === "billingAddress" ? order : "asc"}
                  onClick={() => handleRequestSort("billingAddress")}
                >
                  Cidade
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedClients.map((client) => (
              <TableRow key={client.id}>
                <TableCell>
                  {client.name}
                  <Typography variant="h6" fontSize="15px">
                    {client.email}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <div className="flex justify-center">
                    <div
                      className={`w-3 h-3 rounded-full ${getCompanyColor(
                        client.isCompany
                      )}`}
                    ></div>
                  </div>
                </TableCell>
                <TableCell align="center">
                  {client.seller
                    ? sellers[client.seller] || "Vendedor Desconhecido"
                    : "Nenhum Vendedor Cadastrado"}
                </TableCell>
                <TableCell align="center">
                  {formatPhoneNumber(client.phone)}
                </TableCell>
                {/* <TableCell align="center">{client.email}</TableCell> */}
                <TableCell align="center">
                  <div className="flex justify-center">
                    <div
                      className={`w-3 h-3 rounded-full ${getStatusColor(
                        client.deletedAt,
                        client.emailConfirmed
                      )}`}
                    ></div>
                  </div>
                </TableCell>
                <TableCell align="center">
                  {formatDate(client.createdAt)}
                </TableCell>
                <TableCell
                  align="center"
                  className="whitespace-normal break-words"
                  width={200}
                >
                  {client.billingAddress
                    ? `${client.billingAddress.city}/${client.billingAddress.state}`
                    : "Sem Endereço Cadastrado"}
                </TableCell>
                <TableCell align="center">
                  <IconButton onClick={() => onEdit(client)}>
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredClients.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Linhas por página"
        />
      </TableContainer>
    </div>
  );
};

export default ClientTable;
